import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { RunOnceScheduler } from 'vscode/vscode/vs/base/common/async';
import { Disposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { IAccessibilityService } from 'vscode/vscode/vs/platform/accessibility/common/accessibility.service';
import { CellEditState } from 'vscode/vscode/vs/workbench/contrib/notebook/browser/notebookBrowser';
import { registerNotebookContribution } from 'vscode/vscode/vs/workbench/contrib/notebook/browser/notebookEditorExtensions';
import { outputDisplayLimit } from 'vscode/vscode/vs/workbench/contrib/notebook/browser/viewModel/codeCellViewModel';
import { CellKind } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookCommon';
import { cellRangesToIndexes } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookRange';
import { INotebookService } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookService.service';

let NotebookViewportContribution = class NotebookViewportContribution extends Disposable {
    static { this.id = 'workbench.notebook.viewportWarmup'; }
    constructor(_notebookEditor, _notebookService, accessibilityService) {
        super();
        this._notebookEditor = _notebookEditor;
        this._notebookService = _notebookService;
        this._warmupDocument = null;
        this._warmupViewport = ( new RunOnceScheduler(() => this._warmupViewportNow(), 200));
        this._register(this._warmupViewport);
        this._register(this._notebookEditor.onDidScroll(() => {
            this._warmupViewport.schedule();
        }));
        this._warmupDocument = ( new RunOnceScheduler(() => this._warmupDocumentNow(), 200));
        this._register(this._warmupDocument);
        this._register(this._notebookEditor.onDidAttachViewModel(() => {
            if (this._notebookEditor.hasModel()) {
                this._warmupDocument?.schedule();
            }
        }));
        if (this._notebookEditor.hasModel()) {
            this._warmupDocument?.schedule();
        }
    }
    _warmupDocumentNow() {
        if (this._notebookEditor.hasModel()) {
            for (let i = 0; i < this._notebookEditor.getLength(); i++) {
                const cell = this._notebookEditor.cellAt(i);
                if (cell?.cellKind === CellKind.Markup && cell?.getEditState() === CellEditState.Preview && !cell.isInputCollapsed) ;
                else if (cell?.cellKind === CellKind.Code) {
                    this._warmupCodeCell(cell);
                }
            }
        }
    }
    _warmupViewportNow() {
        if (this._notebookEditor.isDisposed) {
            return;
        }
        if (!this._notebookEditor.hasModel()) {
            return;
        }
        const visibleRanges = this._notebookEditor.getVisibleRangesPlusViewportAboveAndBelow();
        cellRangesToIndexes(visibleRanges).forEach(index => {
            const cell = this._notebookEditor.cellAt(index);
            if (cell?.cellKind === CellKind.Markup && cell?.getEditState() === CellEditState.Preview && !cell.isInputCollapsed) {
                this._notebookEditor.createMarkupPreview(cell);
            }
            else if (cell?.cellKind === CellKind.Code) {
                this._warmupCodeCell(cell);
            }
        });
    }
    _warmupCodeCell(viewCell) {
        if (viewCell.isOutputCollapsed) {
            return;
        }
        const outputs = viewCell.outputsViewModels;
        for (const output of outputs.slice(0, outputDisplayLimit)) {
            const [mimeTypes, pick] = output.resolveMimeTypes(this._notebookEditor.textModel, undefined);
            if (!mimeTypes.find(mimeType => mimeType.isTrusted) || mimeTypes.length === 0) {
                continue;
            }
            const pickedMimeTypeRenderer = mimeTypes[pick];
            if (!pickedMimeTypeRenderer) {
                return;
            }
            if (!this._notebookEditor.hasModel()) {
                return;
            }
            const renderer = this._notebookService.getRendererInfo(pickedMimeTypeRenderer.rendererId);
            if (!renderer) {
                return;
            }
            const result = { type: 1 , renderer, source: output, mimeType: pickedMimeTypeRenderer.mimeType };
            this._notebookEditor.createOutput(viewCell, result, 0, true);
        }
    }
};
NotebookViewportContribution = ( __decorate([
    ( __param(1, INotebookService)),
    ( __param(2, IAccessibilityService))
], NotebookViewportContribution));
registerNotebookContribution(NotebookViewportContribution.id, NotebookViewportContribution);
